import { useMemo } from 'react';

import { useTranslate } from '@src/core/hooks';
import { resolveRescheduleNotice, RescheduleNotice, type DetailedSaleItem } from '@src/domains/sale-items';
import { formatRescheduleAntecedence } from '@src/features/sales/reschedule/utils/formatRescheduleAntecedence';

export type RescheduleNoticeView =
  | {
      kind: typeof RescheduleNotice.Available;
      /** Null when the seller does not cap reschedules — a remaining count would mean nothing. */
      remainingCount: number | null;
      deadlineLabel: string;
    }
  | { kind: typeof RescheduleNotice.DeadlineExpired }
  | { kind: typeof RescheduleNotice.NotReschedulable }
  | { kind: typeof RescheduleNotice.None };

export function useRescheduleNotice(saleItem: DetailedSaleItem): RescheduleNoticeView {
  const { i18n } = useTranslate();

  const settings = saleItem.product.reservationReschedulingSettings;
  const { canReschedule, rescheduleUntil, remainingRescheduleCount } = saleItem.rescheduleInfo;

  const notice = resolveRescheduleNotice({
    allowsRescheduling: settings.allowRescheduling,
    canReschedule,
    rescheduleUntil,
    remainingRescheduleCount,
  });

  return useMemo(() => {
    if (notice !== RescheduleNotice.Available) {
      return { kind: notice };
    }

    return {
      kind: notice,
      remainingCount: settings.maxReschedulingCount === null ? null : remainingRescheduleCount,
      deadlineLabel: i18n('checkout_confirmed_reschedule_deadline_antecedence', {
        antecedence: formatRescheduleAntecedence(settings.reschedulingAntecedenceMinutes ?? 0, i18n),
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n is stable per locale
  }, [notice, settings.maxReschedulingCount, settings.reschedulingAntecedenceMinutes, remainingRescheduleCount]);
}

export default useRescheduleNotice;
